import api from '@/api'

const SET_CLIENTS = 'SET_CLIENTS'
const SET_CLIENTS_COUNT = 'SET_CLIENTS_COUNT'
const SET_CLIENTS_PAGE = 'SET_CLIENTS_PAGE'
const SET_FILTERS_CLIENTS = 'SET_FILTERS_CLIENTS'
const SET_CURRENT_CLIENT = 'SET_CURRENT_CLIENT'
const UNSET_CURRENT_CLIENT = 'UNSET_CURRENT_CLIENT'

const SET_USERS = 'SET_USERS'
const UPDATE_USER = 'UPDATE_USER'
const SET_USERS_COUNT = 'SET_USERS_COUNT'
const SET_FILTERS_USERS = 'SET_FILTERS_USERS'
const SET_CURRENT_USER = 'SET_CURRENT_USER'
const UNSET_CURRENT_USER = 'UNSET_CURRENT_USER'

const SET_CURRENT_FARMS = 'SET_CURRENT_FARMS'
const SET_CLIENT_LIST = 'SET_CLIENT_LIST'
const SET_CLIENTS_FARMS = 'SET_CLIENTS_FARMS'
const APPEND_CLIENTS_FARMS = 'APPEND_CLIENTS_FARMS'

export default {
  namespaced: true,

  state () {
    return {
      filtersClients: {},
      filtersUsers: {},
      clients: [],
      users: [],
      clientPage: 0,
      countClients: 20,
      countUsers: 0,
      currentClient: null,
      currentUser: null,
      currentFarms: null,
      clientList: [],
      clientsFarms: []
    }
  },

  getters: {
    clients: (state) => state.clients,
    countClients: (state) => state.countClients,
    users: (state) => state.users,
    countUsers: (state) => state.countUsers,
    filtersClients: (state) => state.filtersClients,
    filtersUsers: (state) => state.filtersUsers,
    currentClient: (state) => state.currentClient,
    currentUser: (state) => state.currentUser,
    currentFarms: (state) => state.currentFarms,
    clientList: (state) => state.clientList,
    clientsFarms: (state) => state.clientsFarms
  },

  mutations: {
    [SET_CLIENTS] (state, data) {
      state.clients = data
    },

    [SET_CLIENTS_COUNT] (state, count) {
      state.countClients = count
    },

    [SET_CLIENTS_PAGE] (state, count) {
      state.clientPage = count
    },

    [SET_FILTERS_CLIENTS] (state, data) {
      state.filtersClients = data
    },

    [SET_CURRENT_CLIENT] (state, data) {
      state.currentClient = data
    },

    [UNSET_CURRENT_CLIENT] (state) {
      state.currentClient = null
    },

    [SET_USERS] (state, data) {
      state.users = data
    },

    [UPDATE_USER] (state, data) {
      const source = data.user_type === 'consultant'
        ? state.users
        : state.clients
      const index = source
        .map(item => item.id)
        .indexOf(data.id)
      if (index !== -1) {
        for (const key in data) {
          source[index][key] = data[key]
        }
      }
    },

    [SET_USERS_COUNT] (state, count) {
      state.countUsers = count
    },

    [SET_FILTERS_USERS] (state, data) {
      state.filtersUsers = data
    },

    [SET_CURRENT_USER] (state, data) {
      state.currentUser = data
    },

    [UNSET_CURRENT_USER] (state) {
      state.currentUser = null
    },

    [SET_CURRENT_FARMS] (state, data) {
      state.currentFarms = data
    },

    [SET_CLIENT_LIST] (state, data) {
      state.clientList = data
    },

    [SET_CLIENTS_FARMS] (state, data) {
      state.clientsFarms = data
    },

    [APPEND_CLIENTS_FARMS] (state, data) {
      state.clientsFarms = [...state.clientsFarms, ...data]
    }
  },

  actions: {
    async loadClients ({ commit, state }, params = {}) {
      for (const key in params) {
        state.filtersClients[key] = params[key]
      }
      const filters = {}
      for (const key in state.filtersClients) {
        filters[key] = state.filtersClients[key]
      }
      const { data, count } = await api.dashboard.clients.list(filters)
      commit(SET_CLIENTS, data)
      commit(SET_CLIENTS_COUNT, count)
      return data
    },

    async loadClientsNextPage ({ commit, state }) {
      if (state.clientPage * 20 < state.countClients) {
        commit(SET_CLIENTS_PAGE, state.clientPage + 1)
        const params = {
          page: state.clientPage
        }
        const { data, count } = await api.dashboard.clients.list(params)

        // Filter out duplicates based on a unique property (e.g., id)
        const existingIds = new Set(state.clients.map(client => client.id))
        const newClients = data.filter(client => !existingIds.has(client.id))

        commit(SET_CLIENTS, [...state.clients, ...newClients])
        commit(SET_CLIENTS_COUNT, count)
      }
      return state.clients
    },

    async getClient ({ commit }, id) {
      const { data } = await api.dashboard.clients.getById(id)
      commit(SET_CURRENT_CLIENT, data)
    },

    async getFarms ({ commit }, id) {
      const { data, error } = await api.dashboard.clients.getFarmsById(id)
      commit(SET_CURRENT_FARMS, data)
      return error
    },

    async updateClient ({ dispatch }, { id, data }) {
      await api.dashboard.clients.updateById(id, data)
      dispatch('loadClients')
    },

    async loadClientList ({ commit }, params = {}) {
      const response = await api.dashboard.clients.clientList(params)
      if (!response.error) {
        const { data, count, next } = response
        if (!params.offset || params.offset === 0) {
          commit(SET_CLIENT_LIST, data)
        } else {
          const currentList = this.getters['dashboard/clientList'] || []
          commit(SET_CLIENT_LIST, [...currentList, ...data])
        }
        return { data, count, next, error: null }
      }
      return response
    },

    async loadClientsFarms ({ commit }, params) {
      const response = await api.dashboard.clients.clientsFarms(params)
      if (params.offset) {
        commit(APPEND_CLIENTS_FARMS, response.data)
      } else {
        commit(SET_CLIENTS_FARMS, response.data)
      }
      return response
    },

    async loadUsers ({ commit, state }, params = {}) {
      for (const key in params) {
        state.filtersUsers[key] = params[key]
      }
      const filters = {}
      for (const key in state.filtersUsers) {
        filters[key] = state.filtersUsers[key]
      }
      const { data, count } = await api.dashboard.users.list(filters)
      commit(SET_USERS, data)
      commit(SET_USERS_COUNT, count)
    },

    async createUser ({ dispatch }, payload) {
      const { errors } = await api.dashboard.users.create(payload)
      dispatch('loadClients')
      dispatch('loadUsers')
      return errors
    },

    async getUser ({ commit }, id) {
      const { data } = await api.dashboard.users.getById(id)
      commit(SET_CURRENT_USER, data)
      return data
    },

    async updateUser ({ commit }, payload) {
      const { data, errors } = await api.dashboard.users.updateById(payload.id, payload)
      commit(UPDATE_USER, data)
      return errors
    },

    async changeUserPassword ({ getters }, payload) {
      const id = getters.currentUser.id
      const { error } = await api.dashboard.users.updatePasswordById(id, payload)
      return error
    }
  }
}
